/* ZOR — product gallery.
   - Main stage is a native CSS scroll-snap track: momentum swipe on phones,
     arrows / thumbs / keyboard on desktop, all kept in sync.
   - No hover zoom: magnifying only happens on request, in the full-screen viewer.
   - Tap / click opens PhotoSwipe (vendored in assets, MIT): pinch & double-tap
     zoom, pan, swipe between images, swipe down to close, zoom-from-image
     opening animation, plus a ZOR thumbnail strip.
   - 360 videos: muted inline loops that only load and play while on screen
     (poster only with reduced motion); full screen they loop cleanly, no player UI.
   Without JS, every slide is a plain link to the full-size image. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function initGallery(gal) {
  if (gal.dataset.galInit) return;
  gal.dataset.galInit = '1';

  const track = gal.querySelector('[data-gal-track]');
  const slides = Array.from(gal.querySelectorAll('[data-gal-slide]'));
  const thumbs = Array.from(gal.querySelectorAll('[data-gal-goto]'));
  const dots = Array.from(gal.querySelectorAll('.zor-gal__dots i'));
  const prev = gal.querySelector('[data-gal-prev]');
  const next = gal.querySelector('[data-gal-next]');
  const current = gal.querySelector('[data-gal-current]');
  const expand = gal.querySelector('[data-gal-expand]');
  if (!track || !slides.length) return;

  let index = 0;
  const isVideo = (s) => s && s.hasAttribute('data-gal-video');

  /* ---------- index tracking ---------- */
  function setIndex(i) {
    if (i === index && gal.dataset.galPainted) return;
    gal.dataset.galPainted = '1';
    index = i;
    if (current) current.textContent = String(i + 1);
    thumbs.forEach((t, k) => {
      const on = k === i;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
    if (prev) prev.disabled = i === 0;
    if (next) next.disabled = i === slides.length - 1;
    const active = thumbs[i];
    if (active && active.parentElement) {
      const strip = active.parentElement;
      const left = active.offsetLeft - (strip.clientWidth - active.clientWidth) / 2;
      strip.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  function goTo(i, smooth = true) {
    const clamped = Math.max(0, Math.min(slides.length - 1, i));
    track.scrollTo({ left: clamped * track.clientWidth, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
    setIndex(clamped);
  }

  let raf = 0;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      setIndex(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
    });
  }, { passive: true });

  prev && prev.addEventListener('click', () => goTo(index - 1));
  next && next.addEventListener('click', () => goTo(index + 1));
  thumbs.forEach((t, k) => {
    t.addEventListener('click', () => goTo(k));
  });
  track.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
  });
  window.addEventListener('resize', () => goTo(index, false));
  setIndex(0);

  /* ---------- 360 videos: lazy-load, play only while visible ---------- */
  const videoSlides = slides.filter(isVideo);
  function startVideo(slide) {
    const v = slide.querySelector('video');
    if (!v) return;
    const src = v.querySelector('source[data-src]');
    if (src) { src.src = src.dataset.src; src.removeAttribute('data-src'); v.load(); }
    if (reduceMotion) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }
  function stopVideo(slide) {
    const v = slide.querySelector('video');
    if (v && !v.paused) v.pause();
  }
  if (videoSlides.length && 'IntersectionObserver' in window) {
    const vio = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting && !document.hidden ? startVideo(e.target) : stopVideo(e.target)));
    }, { threshold: 0.6 });
    videoSlides.forEach((s) => vio.observe(s));
  } else {
    videoSlides.forEach(startVideo);
  }

  /* Hover magnifier removed at the owner's request (automatic zoom was confusing);
     zoom only happens deliberately, inside the full-screen viewer. */

  /* ---------- one-time "tap to zoom" hint on touch devices ---------- */
  if (!finePointer) {
    let seen = false;
    try { seen = localStorage.getItem('zor-gal-hint') === '1'; } catch (e) { /* storage blocked */ }
    if (!seen) {
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        gal.classList.add('is-hinting');
        setTimeout(() => gal.classList.remove('is-hinting'), 2600);
        try { localStorage.setItem('zor-gal-hint', '1'); } catch (e) { /* ignore */ }
      }, { threshold: 0.6 });
      io.observe(track);
    }
  }

  /* ---------- PhotoSwipe lightbox ---------- */
  let lightbox = null;
  let loading = null;

  function ensureCss() {
    const href = gal.dataset.pswpCss;
    if (!href || document.querySelector(`link[href="${href}"]`)) return;
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    document.head.appendChild(l);
  }

  function load() {
    if (loading) return loading;
    ensureCss();
    loading = import(gal.dataset.pswpLightbox).then(({ default: PhotoSwipeLightbox }) => {
      lightbox = new PhotoSwipeLightbox({
        gallery: track,
        children: '[data-gal-slide]',
        pswpModule: () => import(gal.dataset.pswpCore),
        bgOpacity: 1,
        showHideAnimationType: reduceMotion ? 'none' : 'zoom',
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2.2,
        maxZoomLevel: 4,
        wheelToZoom: true,
        imageClickAction: 'zoom-or-close',
        tapAction: 'toggle-controls',
        doubleTapAction: 'zoom',
        closeOnVerticalDrag: true,
        padding: { top: 56, bottom: 92, left: 0, right: 0 },
        arrowPrevTitle: 'Previous image',
        arrowNextTitle: 'Next image',
        closeTitle: 'Close',
        zoomTitle: 'Zoom'
      });

      // video slides open as a playable video instead of an image
      lightbox.addFilter('itemData', (itemData, i) => {
        const s = slides[i];
        if (!isVideo(s)) return itemData;
        const esc = (u) => String(u || '').replace(/"/g, '&quot;');
        // no width/height: PhotoSwipe then lays the html out at full viewport size
        // instead of offsetting it like a fitted image (which pushed it right)
        return {
          html: `<div class="zor-pswp-video"><video src="${esc(s.dataset.videoSrc)}" poster="${esc(s.dataset.videoPoster)}" autoplay muted loop playsinline disablepictureinpicture disableremoteplayback controlslist="nodownload nofullscreen noremoteplayback"></video></div>`
        };
      });

      // the opening animation grows from the visible slide image
      lightbox.addFilter('thumbEl', (thumbEl, data, i) => {
        const s = slides[i];
        return (s && !isVideo(s) && s.querySelector('img')) || thumbEl;
      });
      lightbox.addFilter('placeholderSrc', (src, slide) => {
        const s = slides[slide.index];
        const img = s && !isVideo(s) && s.querySelector('img');
        return (img && img.currentSrc) || src;
      });

      // ZOR thumbnail strip + first-use hint inside the viewer
      lightbox.on('uiRegister', () => {
        const pswp = lightbox.pswp;
        pswp.ui.registerElement({
          name: 'zorThumbs',
          appendTo: 'wrapper',
          order: 9,
          onInit: (el, p) => {
            if (slides.length < 2) return;
            const strip = document.createElement('div');
            strip.className = 'zor-pswp-thumbs';
            slides.forEach((s, k) => {
              const b = document.createElement('button');
              b.type = 'button';
              b.setAttribute('aria-label', `Show image ${k + 1}`);
              const src = s.querySelector('img');
              const thumb = isVideo(s) ? s.dataset.videoPoster : (src ? src.currentSrc || src.src : '');
              b.innerHTML = `<img src="${thumb}" alt="">`;
              b.addEventListener('click', () => p.goTo(k));
              strip.appendChild(b);
            });
            el.appendChild(strip);
            const paint = () => {
              strip.querySelectorAll('button').forEach((b, k) => b.classList.toggle('is-active', k === p.currIndex));
              const a = strip.children[p.currIndex];
              if (a) strip.scrollTo({ left: a.offsetLeft - (strip.clientWidth - a.clientWidth) / 2, behavior: 'smooth' });
            };
            p.on('change', paint);
            paint();
          }
        });
        pswp.ui.registerElement({
          name: 'zorHint',
          appendTo: 'wrapper',
          order: 10,
          onInit: (el, p) => {
            const hint = document.createElement('div');
            hint.className = 'zor-pswp-hint';
            hint.textContent = finePointer ? 'Click or scroll to zoom' : 'Pinch or double-tap to zoom';
            el.appendChild(hint);
            const hide = () => hint.classList.add('is-gone');
            setTimeout(hide, 2400);
            p.on('zoomPanUpdate', hide);
          }
        });
      });

      // keep the page gallery on the image the shopper ended on
      lightbox.on('change', () => {
        if (lightbox.pswp) goTo(lightbox.pswp.currIndex, false);
      });
      // the page loop pauses behind the viewer; the viewer's videos stop on close
      lightbox.on('openingAnimationStart', () => videoSlides.forEach(stopVideo));
      // play the video on the slide in view, pause the neighbours PhotoSwipe keeps loaded
      // (html slides are attached lazily, so act on PhotoSwipe's per-slide events)
      const viewerVideo = (content) => content && content.element && content.element.querySelector && content.element.querySelector('video');
      const playViewer = (v) => {
        if (!v) return;
        v.muted = true;
        const r = v.play();
        if (r && r.catch) r.catch(() => {});
      };
      lightbox.on('contentActivate', ({ content }) => playViewer(viewerVideo(content)));
      lightbox.on('contentAppend', ({ content }) => {
        const p = lightbox.pswp;
        if (p && content && content.index === p.currIndex) setTimeout(() => playViewer(viewerVideo(content)), 0);
      });
      lightbox.on('contentDeactivate', ({ content }) => {
        const v = viewerVideo(content);
        if (v && !v.paused) v.pause();
      });
      lightbox.on('close', () => document.querySelectorAll('.pswp video').forEach((v) => v.pause()));
      lightbox.on('destroy', () => {
        const s = slides[index];
        if (isVideo(s)) startVideo(s);
      });

      lightbox.init();
      return lightbox;
    });
    return loading;
  }

  function open(i) {
    load().then((lb) => lb.loadAndOpen(i, { gallery: track }));
  }

  // Lightbox handles clicks on the slides once initialised; until then, catch
  // the first click so it never falls through to the raw image link.
  slides.forEach((s, k) => {
    s.addEventListener('click', (e) => {
      if (lightbox) return;
      e.preventDefault();
      open(k);
    });
  });
  expand && expand.addEventListener('click', () => open(index));

  // warm the modules once the shopper shows intent
  ['pointerenter', 'touchstart', 'focusin'].forEach((ev) =>
    gal.addEventListener(ev, () => { load(); }, { once: true, passive: true })
  );
}

function initAll(root = document) {
  root.querySelectorAll('[data-zor-gal]').forEach(initGallery);
}

initAll();
document.addEventListener('shopify:section:load', (e) => initAll(e.target));
